import { useEffect, useRef } from 'react';

const KEYBOARD_HEIGHT_THRESHOLD_PX = 120;
const TOUCH_MOVE_THRESHOLD_PX = 10;
const KEYBOARDLESS_INPUT_TYPES = new Set([
    'button',
    'checkbox',
    'color',
    'file',
    'hidden',
    'image',
    'radio',
    'range',
    'reset',
    'submit',
]);

function getViewportHeight(): number {
    return window.visualViewport?.height ?? window.innerHeight;
}

function isTextEntryElement(element: Element | null): element is HTMLElement {
    if (!(element instanceof HTMLElement)) {
        return false;
    }

    if (element instanceof HTMLTextAreaElement) {
        return !element.disabled && !element.readOnly;
    }

    if (element instanceof HTMLInputElement) {
        return !element.disabled && !element.readOnly && !KEYBOARDLESS_INPUT_TYPES.has(element.type);
    }

    return element.isContentEditable;
}

/**
 * Android WebView intenta mantener visible el input enfocado cuando el teclado
 * está abierto. Si el usuario arrastra la pantalla en ese estado, quitamos el
 * foco para cerrar el teclado y evitar que el WebView y el layout compitan.
 */
export function useDismissKeyboardOnScroll(): void {
    const initialTouchYRef = useRef<number | null>(null);
    const maxViewportHeightRef = useRef<number>(0);

    useEffect(() => {
        const updateMaxViewportHeight = () => {
            maxViewportHeightRef.current = Math.max(
                maxViewportHeightRef.current,
                getViewportHeight(),
                window.innerHeight
            );
        };

        const isKeyboardLikelyVisible = () => {
            updateMaxViewportHeight();
            return maxViewportHeightRef.current - getViewportHeight() > KEYBOARD_HEIGHT_THRESHOLD_PX;
        };

        const blurActiveTextEntry = () => {
            const activeElement = document.activeElement;

            if (!isTextEntryElement(activeElement) || !isKeyboardLikelyVisible()) {
                return;
            }

            activeElement.blur();
            initialTouchYRef.current = null;
        };

        const handleTouchStart = (event: TouchEvent) => {
            initialTouchYRef.current = event.touches[0]?.clientY ?? null;
        };

        const handleTouchMove = (event: TouchEvent) => {
            const initialTouchY = initialTouchYRef.current;
            const currentTouchY = event.touches[0]?.clientY;

            if (initialTouchY === null || currentTouchY === undefined) {
                return;
            }

            if (Math.abs(currentTouchY - initialTouchY) < TOUCH_MOVE_THRESHOLD_PX) {
                return;
            }

            blurActiveTextEntry();
        };

        const handleOrientationChange = () => {
            maxViewportHeightRef.current = 0;
            window.setTimeout(updateMaxViewportHeight, 250);
        };

        updateMaxViewportHeight();

        window.addEventListener('resize', updateMaxViewportHeight);
        window.addEventListener('orientationchange', handleOrientationChange);
        window.visualViewport?.addEventListener('resize', updateMaxViewportHeight);
        document.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: true, capture: true });

        return () => {
            window.removeEventListener('resize', updateMaxViewportHeight);
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.visualViewport?.removeEventListener('resize', updateMaxViewportHeight);
            document.removeEventListener('touchstart', handleTouchStart, { capture: true });
            document.removeEventListener('touchmove', handleTouchMove, { capture: true });
        };
    }, []);
}
